import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { HomeSection, HomeSectionDisplayMode, HomeSectionProduct } from '../types/homeSection';

const BASE = '/admin/v1/home-sections';
const SECTION_REORDER_PATH = `${BASE}/reorder`;

interface ListEnvelope<T> {
  success: boolean;
  data?: T | null;
}

const productsPath = (sectionId: number) => `${BASE}/${sectionId}/products`;

const fetchSections = async (): Promise<HomeSection[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`home sections load failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope<HomeSection[]>;
  return env.data ?? [];
};

const fetchSectionProducts = async (sectionId: number): Promise<HomeSectionProduct[]> => {
  const res = await adminFetch(productsPath(sectionId));
  if (!res.ok) throw new Error(`home section products load failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope<HomeSectionProduct[]>;
  return env.data ?? [];
};

export const homeSectionsQueryKey = () => ['admin', 'home-sections'] as const;
export const homeSectionProductsQueryKey = (sectionId: number) =>
  ['admin', 'home-sections', sectionId, 'products'] as const;

export const useHomeSectionsQuery = () =>
  useQuery<HomeSection[]>({
    queryKey: homeSectionsQueryKey(),
    queryFn: fetchSections,
    staleTime: 60_000,
  });

/** 섹션 상품 목록 쿼리 옵션 — 여러 섹션을 useQueries 로 함께 읽을 때(상품 등록·수정 섹션 노출). */
export const homeSectionProductsQueryOptions = (sectionId: number) => ({
  queryKey: homeSectionProductsQueryKey(sectionId),
  queryFn: () => fetchSectionProducts(sectionId),
});

export const useHomeSectionProductsQuery = (sectionId: number | null) =>
  useQuery<HomeSectionProduct[]>({
    queryKey: homeSectionProductsQueryKey(sectionId ?? -1),
    queryFn: () => fetchSectionProducts(sectionId as number),
    enabled: sectionId != null,
  });

/** 진열 방법(수동/자동) 판별 — 값이 없으면 수동. */
export const isAutoSection = (s: Pick<HomeSection, 'displayMode'>): boolean => s.displayMode === 'AUTO';

/**
 * QA-570 — 섹션 진열 방법 변경. PATCH /{sectionId}/display-mode {displayMode} → 전체 섹션 목록(BE QA-478).
 * 자동으로 바꿔도 수동 매핑은 남는다.
 */
export const useHomeSectionDisplayModeMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sectionId, displayMode }: { sectionId: number; displayMode: HomeSectionDisplayMode }) => {
      const path = `${BASE}/${sectionId}/display-mode`;
      const body = { displayMode };
      const res = await adminFetch(path, {
        method: 'PATCH',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PATCH', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`home section display mode failed: ${res.status}`);
      const env = (await res.json()) as ListEnvelope<HomeSection[]>;
      return env.data ?? [];
    },
    onSuccess: (data) => {
      if (data.length > 0) qc.setQueryData(homeSectionsQueryKey(), data);
      void qc.invalidateQueries({ queryKey: homeSectionsQueryKey() });
    },
  });
};

/**
 * ID44: 섹션 자체 순서 드래그 영속. body {sectionIds:[Long...]} → 응답은 새 순서 전체 목록.
 * /admin/v1/home-sections/reorder 는 writeAllowlist 등록 경로 → Idempotency-Key 필수.
 * 미존재 sectionId 섞이면 BE 404(전체 롤백).
 */
export const useHomeSectionReorderMutation = () => {
  const qc = useQueryClient();
  const reorder = useMutation({
    mutationFn: async (sectionIds: number[]): Promise<HomeSection[]> => {
      const body = { sectionIds };
      const res = await adminFetch(SECTION_REORDER_PATH, {
        method: 'PUT',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('PUT', SECTION_REORDER_PATH, body),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`home section reorder failed: ${res.status}`);
      const env = (await res.json()) as ListEnvelope<HomeSection[]>;
      return env.data ?? [];
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeSectionsQueryKey() });
    },
  });
  return { reorder };
};

export const useHomeSectionProductMutations = () => {
  const qc = useQueryClient();
  const invalidate = (sectionId: number) =>
    qc.invalidateQueries({ queryKey: homeSectionProductsQueryKey(sectionId) });

  const add = useMutation({
    mutationFn: async ({
      sectionId,
      productIds,
    }: {
      sectionId: number;
      productIds: ReadonlyArray<string>;
    }): Promise<HomeSectionProduct[]> => {
      const res = await adminFetch(productsPath(sectionId), {
        method: 'POST',
        body: JSON.stringify({ productIds }),
      });
      if (!res.ok) throw new Error(`home section product add failed: ${res.status}`);
      const env = (await res.json()) as ListEnvelope<HomeSectionProduct[]>;
      return env.data ?? [];
    },
    onSuccess: (_, { sectionId }) => {
      void invalidate(sectionId);
    },
  });

  const reorder = useMutation({
    mutationFn: async ({
      sectionId,
      productIds,
    }: {
      sectionId: number;
      productIds: ReadonlyArray<string>;
    }): Promise<HomeSectionProduct[]> => {
      const res = await adminFetch(`${productsPath(sectionId)}/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ productIds }),
      });
      if (!res.ok) throw new Error(`home section product reorder failed: ${res.status}`);
      const env = (await res.json()) as ListEnvelope<HomeSectionProduct[]>;
      return env.data ?? [];
    },
    onSuccess: (_, { sectionId }) => {
      void invalidate(sectionId);
    },
  });

  const remove = useMutation({
    mutationFn: async ({
      sectionId,
      productId,
    }: {
      sectionId: number;
      productId: string;
    }): Promise<void> => {
      const res = await adminFetch(`${productsPath(sectionId)}/${encodeURIComponent(productId)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`home section product remove failed: ${res.status}`);
    },
    onSuccess: (_, { sectionId }) => {
      void invalidate(sectionId);
    },
  });

  return { add, reorder, remove };
};
